import { Link } from 'react-router-dom';
import type { CheckNumberEdit } from '../../check-batch/components/CheckTable';
import { formatMoney } from '../../check-batch/format';
import { earningsText } from '../helpers';
import type { ContractorBatch, ContractorBatchLine } from '../types';

interface ContractorTableProps {
  batch: ContractorBatch;
  selectedIds: ReadonlySet<string>;
  edit: CheckNumberEdit | null;
  disabled: boolean;
  onToggle: (lineId: string) => void;
  onToggleAll: () => void;
  onStartEdit: (line: ContractorBatchLine) => void;
  onChangeEdit: (value: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onEditPayment: (line: ContractorBatchLine) => void;
  onRemoveRow: (line: ContractorBatchLine) => void;
}

function StatusChip({ batch, line }: { batch: ContractorBatch; line: ContractorBatchLine }) {
  // Until a readiness check has run for the current version nothing is known, so nothing is claimed ("Ready" would be a guess).
  if (!batch.readiness || batch.readiness.isStale) return <span className="wc-chip wc-chip--neutral">Not checked yet</span>;
  return line.rowStatus === 'NeedsAttention'
    ? <span className="wc-chip wc-chip--bad">Needs Attention</span>
    : <span className="wc-chip wc-chip--ok">Ready</span>;
}

export function ContractorTable(props: ContractorTableProps) {
  const { batch, selectedIds, edit, disabled } = props;
  const lines = batch.lines.filter((l) => l.included);
  const removed = batch.lines.length - lines.length;
  const allSelected = lines.length > 0 && lines.every((l) => selectedIds.has(l.id));

  if (lines.length === 0) {
    return (
      <p className="wc-empty" role="status" data-testid="no-lines">
        No contractors are in this batch{removed > 0 ? ` (${removed} removed from this batch)` : ''}. Use “Add contractor payment” to choose who to pay by check.
      </p>
    );
  }

  return (
    <>
      <div className="table-scroll" role="region" aria-label="Contractors paid by check" tabIndex={0}>
        <table className="wc-table" aria-label="Contractors paid by check">
          <thead>
            <tr>
              <th scope="col">
                <input type="checkbox" aria-label="Select all contractors in this batch" checked={allSelected} disabled={disabled} onChange={props.onToggleAll} />
              </th>
              <th scope="col">Contractor</th>
              <th scope="col">Description</th>
              <th scope="col">Pay Type</th>
              <th scope="col">Earnings</th>
              <th scope="col" className="wc-num">Check Amount</th>
              <th scope="col">Check Number</th>
              <th scope="col">Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const editing = edit?.lineId === line.id;
              return (
                <tr key={line.id} data-testid={`contractor-row-${line.id}`}>
                  <td>
                    <input type="checkbox" aria-label={`Select ${line.contractorName}`} checked={selectedIds.has(line.id)} disabled={disabled} onChange={() => props.onToggle(line.id)} />
                  </td>
                  <td>{line.contractorName}{line.businessName && <span className="wc-sub">{line.businessName}</span>}</td>
                  <td className="wc-wrap">{line.description}</td>
                  <td className="wc-wrap">{line.amountMode === 'HoursRate' ? 'Hours × rate' : 'Flat amount'}</td>
                  <td className="wc-wrap">{earningsText(line)}</td>
                  <td className="wc-num wc-amount">{formatMoney(line.amount)}</td>
                  <td>
                    {editing ? (
                      <form
                        className="wc-edit"
                        onSubmit={(e) => {
                          e.preventDefault();
                          props.onCommitEdit();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            props.onCancelEdit();
                          }
                        }}
                      >
                        <input
                          aria-label={`Check number for ${line.contractorName}`}
                          aria-invalid={edit.error ? true : undefined}
                          aria-describedby={edit.error ? `check-number-error-${line.id}` : undefined}
                          value={edit.value}
                          maxLength={20}
                          disabled={edit.saving}
                          data-autofocus
                          autoFocus
                          onChange={(e) => props.onChangeEdit(e.target.value)}
                        />
                        <button type="submit" className="wc-btn wc-btn--mini" disabled={edit.saving}>Save</button>
                        <button type="button" className="wc-btn wc-btn--mini wc-btn--ghost" disabled={edit.saving} onClick={props.onCancelEdit}>Cancel</button>
                        {edit.error && <span id={`check-number-error-${line.id}`} className="error wc-edit__error" role="alert">{edit.error}</span>}
                      </form>
                    ) : (
                      <span className="wc-checknum">
                        <span data-testid={`check-number-${line.id}`}>{line.checkNumber}</span>
                        <span className="wc-sub" data-testid={`check-source-${line.id}`}>{line.checkNumberSource === 'Manual' ? 'Set by you' : 'Auto'}</span>
                        <button type="button" className="wc-icon-btn" aria-label={`Edit check number for ${line.contractorName}`} disabled={disabled} onClick={() => props.onStartEdit(line)}>✎</button>
                      </span>
                    )}
                  </td>
                  <td><StatusChip batch={batch} line={line} /></td>
                  <td>
                    <span className="wc-actions">
                      <button type="button" className="wc-btn wc-btn--mini wc-btn--ghost" aria-label={`Edit payment for ${line.contractorName}`} disabled={disabled} onClick={() => props.onEditPayment(line)}>Edit</button>
                      <Link className="wc-icon-btn wc-icon-btn--view" aria-label={`View ${line.contractorName}`} to={`/people/contractors/${line.contractorId}`}>👁</Link>
                      <button type="button" className="wc-icon-btn wc-icon-btn--remove" aria-label={`Remove ${line.contractorName} from this batch`} disabled={disabled} onClick={() => props.onRemoveRow(line)}>🗑</button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {removed > 0 && (
        <p className="wc-muted" data-testid="removed-note">
          {removed} {removed === 1 ? 'contractor was' : 'contractors were'} removed from this batch. Their contractor records are unchanged.
        </p>
      )}
    </>
  );
}
